import { createInertiaApp } from "@inertiajs/react"
import { createElement, ReactNode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"
import { BreadcrumbData } from "../pages/Admin/Layout"
import PublicLayout from "../pages/shared/PublicLayout"

// Opt out of the browser's automatic scroll restoration. Some browsers
// (notably iOS Chrome on short pages) use this path to reset scrollY to 0
// during toolbar-reveal resize cascades. Managing scroll ourselves (or
// letting nothing manage it) is less jumpy.
// See docs/bugs/2026-04-21-ios-chrome-checkout-bouncy-scroll.md.
if (typeof window !== "undefined" && "scrollRestoration" in history) {
  history.scrollRestoration = "manual"
}

// Eruda: in-page devtools for mobile debugging.
// Enable with ?debug=1 (persists via localStorage), disable with ?debug=0.
if (typeof window !== "undefined") {
  const debugParam = new URLSearchParams(window.location.search).get("debug")
  if (debugParam === "1") localStorage.setItem("eruda", "1")
  if (debugParam === "0") localStorage.removeItem("eruda")

  if (localStorage.getItem("eruda") === "1") {
    const script = document.createElement("script")
    script.src = "https://cdn.jsdelivr.net/npm/eruda"
    script.onload = () => {
      const eruda = (window as unknown as { eruda?: { init: () => void } })
        .eruda
      eruda?.init()
    }
    document.documentElement.appendChild(script)
  }
}

// Temporary type definition, until @inertiajs/react provides one
type PageComponent = {
  breadcrumbs?: (props: any) => BreadcrumbData[]
}

type ResolvedComponent = {
  default: ReactNode & PageComponent
  layout?: (page: ReactNode) => ReactNode
}

function isPublicPage(name: string): boolean {
  return (
    !name.startsWith("Admin/") &&
    !name.startsWith("Manage/") &&
    // The capture viewport is the card. PublicLayout would be clipped into the PNG.
    name !== "User/CoffeeJournals/ShareCapture" &&
    name !== "shared/PublicLayout" &&
    name !== "shared/Header"
  )
}

createInertiaApp({
  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  title: (title) => (title ? `${title} | petakopi.my` : "petakopi.my"),

  resolve: async (name) => {
    const pages = import.meta.glob<ResolvedComponent>("../pages/**/*.tsx", {
      eager: false,
    })
    const pageLoader = pages[`../pages/${name}.tsx`]
    if (!pageLoader) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // Await the dynamic import
    const page = await pageLoader()

    // Use default layout for all Admin pages
    if (name.startsWith("Admin/") && name !== "Admin/Layout") {
      const { default: Layout } = await import("../pages/Admin/Layout")
      const PageComponent = page.default as any

      ;(page.default as any).layout = (page: ReactNode) => {
        // Extract props from the page element
        const pageElement = page as any
        const breadcrumbs =
          PageComponent.breadcrumbs && pageElement?.props
            ? PageComponent.breadcrumbs(pageElement.props)
            : undefined

        return createElement(Layout, { breadcrumbs, children: page })
      }
    }

    // Use default layout for all Manage pages
    if (name.startsWith("Manage/") && name !== "Manage/Layout") {
      const { default: ManageLayout } = await import("../pages/Manage/Layout")

      ;(page.default as any).layout = (page: ReactNode) => {
        return createElement(ManageLayout, { children: page })
      }
    }

    // Use PublicLayout for all public pages (not Admin, not Manage).
    // Static import so the layout chunk starts with inertia.js instead of
    // waiting for the page module to resolve.
    if (isPublicPage(name)) {
      ;(page.default as any).layout = (page: ReactNode) => {
        return createElement(PublicLayout, { children: page })
      }
    }

    return page
  },

  setup({ el, App, props }) {
    if (el) {
      if (el.hasChildNodes()) {
        hydrateRoot(el, createElement(App, props))
      } else {
        createRoot(el).render(createElement(App, props))
      }
    } else {
      console.error(
        "Missing root element.\n\n" +
          "If you see this error, it probably means you load Inertia.js on non-Inertia pages.\n" +
          'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.'
      )
    }
  },
})
